/**
 * Pure helper that maps the player state machine onto the flower's
 * vertical position, state attribute, and phase tint in the Ring
 * Player design.
 *
 * Layer percentages mirror the prototype:
 *   Level III (Deep)    —  18
 *   Level II  (Central) —  50
 *   Level I   (Entry)   —  82
 *   Rest (below Entry)  — 100
 *
 * Returns:
 *   { topPct, stateName, activeLayer, phase?, breathe? }
 *
 * where:
 *   topPct      — number 0..100, binds to `.dotWrap { top: X% }`
 *   stateName   — one of 'relax'|'squeeze'|'hold'|'release'
 *                 (matches `.dotWrap[data-state="..."]` CSS)
 *   activeLayer — 0|1|2|3; which horizontal line(s) light up. 0 = none.
 *   phase       — 'a' | 'b' | undefined; for alt exercises where phase
 *                 distinction is communicated via petal tint
 *                 (e.g. directional-engage alts like Front wall / Back
 *                 wall where both phases are contractions).
 *   breathe     — true | undefined; signals an active RELAX hold so
 *                 the CSS can overlay a gentle breath-pulse on the
 *                 petals instead of rendering a static sage bloom.
 */
import { SESSION_STATES } from '@/utils/playerEngine';

export const LAYER_PCT = { 3: 18, 2: 50, 1: 82, rest: 100 };

/**
 * Read an optional `layer` field (1 | 2 | 3) off an exercise. Defaults
 * to 3 when unset — matches the current behavior where every SQUEEZE
 * hold rises to the deepest layer. Product can progressively assign
 * specific layers to exercises (Entry-only for early weeks, Central
 * for mid-program, Deep for advanced) without a schema change.
 *
 * Any value outside 1/2/3 falls back to 3.
 */
function resolveLayer(exercise, fallback = 3) {
  const l = exercise?.layer;
  if (l === 1 || l === 2 || l === 3) return l;
  return fallback;
}

/**
 * Classify an alt exercise into one of three visual sub-kinds based on
 * its altA / altB labels and movement type. The engine treats all alt
 * exercises the same (phase A = squeeze, phase B = relax/push), but the
 * Ring Player's vertical metaphor doesn't land the same way for every
 * kind, so we disambiguate here.
 *
 *   'press-release'       — altA squeezes, altB releases. E.g. Press / Release.
 *   'directional-engage'  — both phases are contractions in different
 *                           anatomical directions. E.g. Front wall / Back wall.
 *                           Phase B is tinted mauve so the user can tell
 *                           which wall they're working.
 *   'rock-release'        — release-based postural rock. E.g. Forward / Back
 *                           pelvic tilt. Neither phase is a contraction; the
 *                           flower hovers near rest and oscillates gently.
 */
export function classifyAlt(exercise) {
  const a = String(exercise?.altA ?? '').toLowerCase();
  const b = String(exercise?.altB ?? '').toLowerCase();
  const mv = String(exercise?.movement ?? '').toLowerCase();

  if (mv === 'relax' || mv === 'release') return 'rock-release';
  if (/wall/.test(a) || /wall/.test(b)) return 'directional-engage';
  if (/press|close|squeeze/.test(a) && /release|open|soften|lengthen/.test(b)) return 'press-release';
  // Default: treat as press-release. Matches the engine's built-in
  // phase-B-is-relax assumption.
  return 'press-release';
}

export function targetLayer(sessionState, exercise, ctx, mode) {
  const type = exercise?.type;
  const c = ctx || { setIdx: 0, repIdx: 0, altPhase: 0 };

  // Non-active phases sit at rest with a neutral petal scale.
  if (
    sessionState === SESSION_STATES.IDLE ||
    sessionState === SESSION_STATES.INSTRUCTION ||
    sessionState === SESSION_STATES.BREATHE ||
    sessionState === SESSION_STATES.COUNTDOWN ||
    sessionState === SESSION_STATES.REST ||
    sessionState === SESSION_STATES.DONE
  ) {
    return { topPct: LAYER_PCT.rest, stateName: 'relax', activeLayer: 0 };
  }

  // RELEASE — flower blooms sage as it descends to rest.
  if (sessionState === SESSION_STATES.RELEASE) {
    return { topPct: LAYER_PCT.rest, stateName: 'release', activeLayer: 0 };
  }

  // ACTIVE branches ────────────────────────────────────────────────
  if (sessionState === SESSION_STATES.ACTIVE) {
    if (type === 'pulse') {
      // Pulse peak defaults to Level II; overridable via exercise.layer
      // (e.g. light Entry-layer pulse → layer: 1).
      const layer = resolveLayer(exercise, 2);
      return { topPct: LAYER_PCT[layer], stateName: 'squeeze', activeLayer: layer };
    }

    if (type === 'alt') {
      const kind = classifyAlt(exercise);
      if (kind === 'rock-release') {
        // Neither phase is a contraction. Phase A = subtle lift to
        // Entry (reads as "open / forward tilt"). Phase B = rest
        // (reads as "neutral / back tilt"). Both blooming sage so the
        // flower stays in the release palette throughout. Rocking is
        // intentionally a fixed layer-I lift, so `exercise.layer` is
        // ignored here — depth semantics don't apply to tilts.
        if (c.altPhase === 0) {
          return { topPct: LAYER_PCT[1], stateName: 'release', activeLayer: 1 };
        }
        return { topPct: LAYER_PCT.rest, stateName: 'release', activeLayer: 0 };
      }
      if (kind === 'directional-engage') {
        // Both phases are contractions at the same layer. Default III;
        // overridable so "Front wall / Back wall" at an earlier layer
        // is possible (e.g. Central-wall alternation → layer: 2).
        const layer = resolveLayer(exercise);
        const phase = c.altPhase === 0 ? 'a' : 'b';
        return { topPct: LAYER_PCT[layer], stateName: 'hold', activeLayer: layer, phase };
      }
      // press-release (default). Phase A target layer; phase B descends
      // to rest regardless of layer.
      if (c.altPhase === 0) {
        const layer = resolveLayer(exercise);
        return { topPct: LAYER_PCT[layer], stateName: 'hold', activeLayer: layer };
      }
      return { topPct: LAYER_PCT.rest, stateName: 'release', activeLayer: 0 };
    }

    // hold / hold-push / timed — the mode carries the movement intent.
    if (mode === 'relax' || mode === 'push') {
      // Relax / push-out holds stay at rest regardless of `layer`;
      // depth semantics don't apply to softening/bearing-down
      // movements. `breathe` enables a petal-scale oscillation during
      // relax holds so the screen doesn't read as frozen.
      const out = { topPct: LAYER_PCT.rest, stateName: 'release', activeLayer: 0 };
      if (mode === 'relax') out.breathe = true;
      return out;
    }
    // Squeeze hold. Default is Level III (full contraction); an
    // exercise with layer: 1 lifts the flower only to Entry, layer: 2
    // to Central. Makes the 3-layer stage meaningful across the 17
    // squeeze exercises instead of all landing at III.
    const layer = resolveLayer(exercise);
    return { topPct: LAYER_PCT[layer], stateName: 'hold', activeLayer: layer };
  }

  return { topPct: LAYER_PCT.rest, stateName: 'relax', activeLayer: 0 };
}
